import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/context/authStore';
import Layout from '@/components/Layout';
import AdminLayout from '@/components/AdminLayout';
import ErrorBoundary from '@/components/ErrorBoundary';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import ForceChangePassword from '@/pages/ForceChangePassword';
import Dashboard from '@/pages/Dashboard';
import Products from '@/pages/Products';
import Inventory from '@/pages/Inventory';
import Alerts from '@/pages/Alerts';
import Categories from '@/pages/Categories';
import Tenants from '@/pages/Tenants';
import Users from '@/pages/Users';
import Sales from '@/pages/Sales';
import Loyalty from '@/pages/Loyalty';
import CashRegister from '@/pages/CashRegister';
import Suppliers from '@/pages/Suppliers';
import PurchaseOrders from '@/pages/PurchaseOrders';
import StoreProfile from '@/pages/StoreProfile';
import Audit from '@/pages/Audit';
import Reports from '@/pages/Reports';
import Repricing from '@/pages/Repricing';
import Combos from '@/pages/Combos';
import Fraccionados from '@/pages/Fraccionados';
import Storefront from '@/pages/store/Storefront';
import Orders from '@/pages/Orders';
import Finances from '@/pages/Finances';
import Billing from '@/pages/Billing';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import SubscriptionFailure from '@/pages/SubscriptionFailure';
// Admin pages
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminTenants from '@/pages/admin/AdminTenants';
import AdminSubscriptions from '@/pages/admin/AdminSubscriptions';
import AdminExpenses from '@/pages/admin/AdminExpenses';
import AdminTenantUsers from '@/pages/admin/AdminTenantUsers';
import AdminBillingSettings from '@/pages/admin/AdminBillingSettings';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si debe cambiar contrasena y no esta en la pagina de cambio, redirigir
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) {
    return <>{children}</>;
  }
  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  if (user?.role === 'superadmin') {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function LandingRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) {
    return <>{children}</>;
  }
  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  if (user?.role === 'superadmin') {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function ChangePasswordRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.must_change_password) {
    if (user?.role === 'superadmin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
    <Routes>
      <Route
        path="/"
        element={
          <LandingRoute>
            <Landing />
          </LandingRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/change-password"
        element={
          <ChangePasswordRoute>
            <ForceChangePassword />
          </ChangePasswordRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="products" element={<Products />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="categories" element={<Categories />} />
        <Route path="combos" element={<Combos />} />
        <Route path="fraccionados" element={<Fraccionados />} />
        <Route path="sales" element={<Sales />} />
        <Route path="vip" element={<Loyalty />} />
        <Route path="orders" element={<Orders />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="users" element={<Users />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="cash-register" element={<CashRegister />} />
        <Route path="finances" element={<Finances />} />
        <Route path="store-profile" element={<StoreProfile />} />
        <Route path="audit" element={<Audit />} />
        <Route path="reports" element={<Reports />} />
        <Route path="repricing" element={<Repricing />} />
        <Route path="billing" element={<Billing />} />
      </Route>
      {/* Tienda online pública (sin login) */}
      <Route path="/tienda/:slug" element={<Storefront />} />

      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/failure" element={<SubscriptionFailure />} />
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <AdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="tenants" element={<AdminTenants />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="expenses" element={<AdminExpenses />} />
        <Route path="users" element={<AdminTenantUsers />} />
        <Route path="billing-settings" element={<AdminBillingSettings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}
